import React from 'react';
import { WarningModal } from '@/src/components/Antd';
import { useI18n } from '@/src/hooks/useRouterChange';
import { duplicatePaymentRefund } from '@/src/request/order/refund';
import { i18nGet } from '@/src/tools/tools';
import {
  CustomerPaymentItem,
  CustomerPaymentStatus,
} from '@/src/types/common/paymentGateway';
import { useRequest } from 'ahooks';
import { SectionWrapper } from 'src/components/Layouts';
import { canRefund } from './config';
import css from './css.module.less';
import PaymentItem from './PaymentItem';

interface CustomerPaymentHistoryProps {
  orderUuid: string;
  items: CustomerPaymentItem[];
  currencyCode?: string;
  decimalPosition?: number;
  canProcessRefund: boolean;
  onUpdate: (data: any) => void;
  onRefresh: () => Promise<any>;
}

const POLLING_INTERVAL = 3000;
const POLLING_TIMEOUT = 60000;

const getPaymentItems = (data: any): CustomerPaymentItem[] => {
  const items = data?.customerPaymentHistory?.items;
  return Array.isArray(items) ? items : [];
};

const getPaymentStatus = (data: any, transactionUuid: string) => {
  return getPaymentItems(data).find(
    (item) => item?.paymentDetail?.transactionUuid === transactionUuid,
  )?.paymentDetail?.status;
};

const getCreatedDateTimestamp = (item: CustomerPaymentItem) => {
  const timestamp = Date.parse(item?.paymentDetail?.createdDate || '');
  return Number.isNaN(timestamp) ? Number.MAX_SAFE_INTEGER : timestamp;
};

const CustomerPaymentHistory = ({
  orderUuid,
  items,
  currencyCode,
  decimalPosition,
  canProcessRefund,
  onUpdate,
  onRefresh,
}: CustomerPaymentHistoryProps) => {
  const i18n = useI18n();
  const getI18n = (key: string) => i18nGet(i18n, key);
  const [refundItem, setRefundItem] =
    React.useState<CustomerPaymentItem | null>(null);
  const pollingTimerRef = React.useRef<number | null>(null);
  const pollingStartedAtRef = React.useRef<number | null>(null);
  const pollingTransactionUuidRef = React.useRef<string | null>(null);
  const submittingRef = React.useRef(false);
  const mountedRef = React.useRef(true);

  const sortedItems = React.useMemo(() => {
    return [...(items || [])].sort(
      (a, b) => getCreatedDateTimestamp(a) - getCreatedDateTimestamp(b),
    );
  }, [items]);

  const stopPolling = React.useCallback(() => {
    if (pollingTimerRef.current !== null) {
      window.clearTimeout(pollingTimerRef.current);
    }
    pollingTimerRef.current = null;
    pollingStartedAtRef.current = null;
    pollingTransactionUuidRef.current = null;
  }, []);

  const startPolling = React.useCallback(
    (transactionUuid: string) => {
      stopPolling();
      pollingStartedAtRef.current = Date.now();
      pollingTransactionUuidRef.current = transactionUuid;

      const scheduleNextPoll = () => {
        const startedAt = pollingStartedAtRef.current;
        const elapsed =
          startedAt === null ? POLLING_TIMEOUT : Date.now() - startedAt;

        if (
          !mountedRef.current ||
          pollingTransactionUuidRef.current !== transactionUuid ||
          elapsed >= POLLING_TIMEOUT
        ) {
          stopPolling();
          return;
        }

        pollingTimerRef.current = window.setTimeout(async () => {
          pollingTimerRef.current = null;

          try {
            const response = await onRefresh();

            if (
              !mountedRef.current ||
              pollingTransactionUuidRef.current !== transactionUuid
            ) {
              return;
            }

            const status = getPaymentStatus(response, transactionUuid);
            if (status === CustomerPaymentStatus.REFUNDING) {
              scheduleNextPoll();
            } else {
              stopPolling();
            }
          } catch {
            stopPolling();
          }
        }, POLLING_INTERVAL);
      };

      scheduleNextPoll();
    },
    [onRefresh, stopPolling],
  );

  const { run: runDuplicatePaymentRefund, loading: refundLoading } = useRequest(
    duplicatePaymentRefund,
    {
      manual: true,
      onSuccess: (response, params) => {
        submittingRef.current = false;
        setRefundItem(null);
        onUpdate(response);

        const transactionUuid = params[0]?.transactionUuid;
        if (
          transactionUuid &&
          getPaymentStatus(response, transactionUuid) ===
            CustomerPaymentStatus.REFUNDING
        ) {
          startPolling(transactionUuid);
        } else {
          stopPolling();
        }
      },
      onError: () => {
        submittingRef.current = false;
      },
    },
  );

  React.useEffect(() => {
    return () => {
      mountedRef.current = false;
      stopPolling();
    };
  }, [stopPolling]);

  React.useEffect(() => {
    const transactionUuid = pollingTransactionUuidRef.current;
    if (!transactionUuid) {
      return;
    }

    const item = (items || []).find(
      (paymentItem) =>
        paymentItem?.paymentDetail?.transactionUuid === transactionUuid,
    );
    if (
      !item ||
      item.paymentDetail.status !== CustomerPaymentStatus.REFUNDING
    ) {
      stopPolling();
    }
  }, [items, stopPolling]);

  const handleRefund = (item: CustomerPaymentItem) => {
    if (!refundLoading) {
      setRefundItem(item);
    }
  };

  const handleRefundConfirm = () => {
    if (
      !refundItem ||
      !canProcessRefund ||
      refundLoading ||
      submittingRef.current
    ) {
      return;
    }

    const transactionUuid = refundItem.paymentDetail.transactionUuid;
    const latestItem =
      (items || []).find(
        (item) => item.paymentDetail.transactionUuid === transactionUuid,
      ) || refundItem;

    if (!canRefund(latestItem)) {
      setRefundItem(null);
      return;
    }

    submittingRef.current = true;
    runDuplicatePaymentRefund({
      orderUuid,
      transactionUuid: latestItem.paymentDetail.transactionUuid,
      paymentMethod: latestItem.paymentMethod,
      gatewayProvider: latestItem.provider,
      paymentMethodUuid: latestItem.paymentDetail.paymentMethodUuid,
    }).catch(() => undefined);
  };

  const handleRefundCancel = () => {
    if (!refundLoading) {
      setRefundItem(null);
    }
  };

  if (!sortedItems.length) {
    return null;
  }

  return (
    <React.Fragment>
      <SectionWrapper
        title={getI18n('allOrders.orderDetail.paymentHistory_title')}
        subtitle={getI18n('allOrders.orderDetail.paymentHistory_subtitle')}
        containerClassName={css.containerClassName}>
        {sortedItems.map((item, index) => (
          <PaymentItem
            key={item.paymentDetail.transactionUuid}
            item={item}
            index={index}
            currencyCode={currencyCode}
            decimalPosition={decimalPosition}
            canProcessRefund={canProcessRefund}
            onRefund={handleRefund}
          />
        ))}
      </SectionWrapper>

      <WarningModal
        visible={Boolean(refundItem)}
        width={600}
        header={getI18n(
          'allOrders.orderDetail.paymentHistory_refund_confirm_title',
        )}
        okText={getI18n('general.confirm')}
        cancelText={getI18n('general.cancel')}
        loading={refundLoading}
        onOk={handleRefundConfirm}
        onCancel={handleRefundCancel}>
        <p className={css.refundConfirmation}>
          {getI18n(
            'allOrders.orderDetail.paymentHistory_refund_confirm_content',
          )}
        </p>
      </WarningModal>
    </React.Fragment>
  );
};

export default CustomerPaymentHistory;
